<script lang="ts">
  import { detectWebGPU, type GpuSupport } from '$lib/gpu/device'
  import { decodeImage, type DecodedImage } from '$lib/image/decode'
  import {
    ConversionCancelled,
    ConversionFailed,
    createPipelineClient,
    type PipelineClient,
  } from '$lib/pipeline/client'
  import {
    DEFAULT_PARAMS,
    STAGE_LABELS,
    type CropRect,
    type IterationProgress,
    type PipelineParams,
    type PipelineStage,
    type PreviewFrame,
    type VectorizationResult,
  } from '$lib/types'
  import CropSelector from '$ui/CropSelector.svelte'
  import LivePreview from '$ui/LivePreview.svelte'
  import ParameterPanel from '$ui/ParameterPanel.svelte'
  import ResultView from '$ui/ResultView.svelte'
  import UnsupportedBrowser from '$ui/UnsupportedBrowser.svelte'
  import UploadZone from '$ui/UploadZone.svelte'

  let support = $state<GpuSupport | null>(null)
  let image = $state<DecodedImage | null>(null)
  let imageName = $state('')
  /** Region to convert, in source pixels; null converts the whole image. */
  let crop = $state<CropRect | null>(null)
  /** The crop the current result was produced from, so the comparison lines up. */
  let resultCrop = $state<CropRect | null>(null)
  let error = $state('')

  let params = $state<PipelineParams>({ ...DEFAULT_PARAMS })
  let result = $state<VectorizationResult | null>(null)
  let stage = $state<PipelineStage | null>(null)
  /** Extra wording from a stage, e.g. the neural models' download progress. */
  let stageDetail = $state('')
  let running = $state(false)
  let preview = $state<PreviewFrame | null>(null)
  let progress = $state<IterationProgress | null>(null)
  /** Set when a failure is worth offering a lower-resolution retry for. */
  let retryable = $state(false)

  const RESOLUTIONS = [512, 768, 1024]

  /**
   * Large sources are not rejected — they are vectorized at the working
   * resolution and the SVG is scaled back up — but saying so avoids the
   * impression that detail was silently lost.
   */
  const converted = $derived(
    image ? (crop ?? { x: 0, y: 0, width: image.width, height: image.height }) : null,
  )

  const sizeNotice = $derived(
    converted && Math.max(converted.width, converted.height) > params.workingResolution * 2
      ? `${crop ? 'This crop' : 'This image'} is ${converted.width} × ${converted.height}. It ` +
        `will be traced at ${params.workingResolution} px on its longest side and the SVG ` +
        'scaled back up.'
      : '',
  )

  // Probed once at startup; the app is unusable without a GPU adapter.
  detectWebGPU().then((value) => (support = value))

  // Created lazily so the worker is not spawned on browsers that fail the gate.
  let client: PipelineClient | null = null
  function pipeline(): PipelineClient {
    client ??= createPipelineClient()
    return client
  }

  async function onSelect(file: File) {
    error = ''
    try {
      const decoded = await decodeImage(file)
      image?.bitmap.close()
      image = decoded
      imageName = file.name
      result = null
      resultCrop = null
      // The crop is a region of the old image; it means nothing for a new one.
      crop = null
      retryable = false
    } catch (cause) {
      error = cause instanceof Error ? cause.message : String(cause)
    }
  }

  async function convert() {
    if (!image || running) return
    error = ''
    retryable = false
    result = null
    preview = null
    progress = null
    running = true
    stage = 'preprocess'
    stageDetail = ''
    try {
      // The worker consumes the bitmap it is handed, so it gets a copy and the
      // preview keeps the original.
      const copy = await createImageBitmap(image.bitmap)
      const requested = crop
      result = await pipeline().run(copy, params, {
        crop: requested,
        onStage: (value, detail) => {
          stage = value
          stageDetail = detail ?? ''
        },
        onIteration: (value) => (progress = value),
        onPreview: (frame) => (preview = frame),
      })
      resultCrop = requested
    } catch (cause) {
      if (!(cause instanceof ConversionCancelled)) {
        error = cause instanceof Error ? cause.message : String(cause)
        retryable = cause instanceof ConversionFailed && cause.recoverable
      }
    } finally {
      running = false
      stage = null
      stageDetail = ''
      preview = null
      progress = null
    }
  }

  function cancel() {
    pipeline().cancel()
  }

  function reset() {
    image?.bitmap.close()
    image = null
    imageName = ''
    error = ''
    retryable = false
    result = null
    resultCrop = null
    crop = null
  }

  /** Drops to the next resolution down and tries again, after a GPU failure. */
  function retrySmaller() {
    const index = RESOLUTIONS.indexOf(params.workingResolution)
    params.workingResolution = RESOLUTIONS[Math.max(0, index - 1)]
    void convert()
  }
</script>

<main>
  <header>
    <h1>Line Art Vectorizer</h1>
    <p>Convert a raster drawing into editable SVG curves, entirely in your browser.</p>
  </header>

  {#if support === null}
    <p class="status" data-testid="detecting">Checking graphics support…</p>
  {:else if !support.supported}
    <UnsupportedBrowser reason={support.reason} detail={support.detail} />
  {:else}
    <section class="workspace" data-testid="workspace">
      {#if image}
        <div class="columns">
          <div class="column">
            {#if running && stage === 'optimize'}
              <LivePreview frame={preview} {progress} />
            {:else if result}
              <ResultView {result} name={imageName} {image} crop={resultCrop} />
            {:else}
              <CropSelector
                {image}
                name={imageName}
                {crop}
                oncrop={(next) => (crop = next)}
              />
            {/if}
          </div>

          <aside class="column sidebar">
            <ParameterPanel bind:params disabled={running} />

            <div class="actions">
              {#if running}
                <button type="button" class="primary" onclick={cancel} data-testid="cancel-button">
                  Cancel
                </button>
              {:else}
                <button type="button" class="primary" onclick={convert} data-testid="convert-button">
                  {result ? 'Convert again' : 'Convert'}
                </button>
              {/if}
              <button type="button" onclick={reset} data-testid="reset-button" disabled={running}>
                Choose a different image
              </button>
            </div>

            {#if running && stage}
              <p class="status" role="status" data-testid="stage-label">
                {stageDetail || STAGE_LABELS[stage]}
              </p>
            {/if}

            {#if !running && sizeNotice}
              <p class="notice" data-testid="size-notice">{sizeNotice}</p>
            {/if}
          </aside>
        </div>
      {:else}
        <UploadZone onselect={onSelect} onerror={(message) => (error = message)} />
      {/if}

      {#if error}
        <div class="error" role="alert" data-testid="error-message">
          <p>{error}</p>
          {#if retryable && params.workingResolution > RESOLUTIONS[0]}
            <button type="button" onclick={retrySmaller} data-testid="retry-smaller">
              Retry at a lower resolution
            </button>
          {/if}
        </div>
      {/if}
    </section>
  {/if}

  <footer data-testid="credits">
    <p>
      Implements
      <cite>
        <a
          href="https://doi.org/10.1145/3799902.3811211"
          target="_blank"
          rel="noreferrer"
          data-testid="paper-link"
        >2D Gaussian Splatting for Bézier Spline Line Art Vectorization</a>
      </cite>
      — Tianhao Chen, Clara Fernandez, Marteinn Oskarsson, Chuck Tappan,
      Olga Sorkine-Hornung, Markus Gross, Christopher Schroers and
      Abdelaziz Djelouah, SIGGRAPH Conference Papers '26.
    </p>
    <p class="fine">
      Stage 1 of the paper — clean line art with variable-width strokes. An
      independent implementation, not affiliated with the authors.
    </p>
  </footer>
</main>

<style>
  main {
    max-width: 76rem;
    margin: 0 auto;
    padding: 2rem 1.25rem 4rem;
  }

  header h1 {
    margin: 0 0 0.25rem;
    font-size: 1.6rem;
  }

  header p {
    margin: 0 0 2rem;
    color: var(--text-muted);
  }

  .workspace {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 22rem;
    gap: 1.25rem;
    align-items: start;
  }

  @media (max-width: 60rem) {
    .columns {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .column {
    min-width: 0;
  }

  .sidebar {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .status {
    color: var(--text-muted);
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .actions button {
    padding: 0.45rem 0.9rem;
    background: var(--surface-raised);
    border: 1px solid var(--border-strong);
    border-radius: 8px;
  }

  .actions button.primary {
    background: var(--accent-strong);
    border-color: var(--accent-strong);
    color: #fff;
  }

  .actions button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .notice {
    margin: 0;
    padding: 0.6rem 0.8rem;
    color: var(--text-muted);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 0.85rem;
  }

  .error p {
    margin: 0;
  }

  .error button {
    margin-top: 0.6rem;
    padding: 0.35rem 0.75rem;
    color: inherit;
    background: transparent;
    border: 1px solid currentColor;
    border-radius: 6px;
  }

  footer {
    margin-top: 3rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--border);
    color: var(--text-muted);
    font-size: 0.85rem;
  }

  footer p {
    margin: 0 0 0.4rem;
    max-width: 60ch;
  }

  footer .fine {
    margin: 0;
    opacity: 0.8;
  }

  footer cite {
    font-style: italic;
  }

  footer a {
    color: var(--accent);
    text-decoration-color: color-mix(in srgb, var(--accent) 45%, transparent);
    text-underline-offset: 2px;
  }

  footer a:hover {
    text-decoration-color: currentColor;
  }

  .error {
    margin: 0;
    padding: 0.75rem 1rem;
    color: var(--danger);
    background: color-mix(in srgb, var(--danger) 12%, transparent);
    border: 1px solid color-mix(in srgb, var(--danger) 35%, transparent);
    border-radius: 8px;
  }
</style>
